
import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';

import { combineLatest, distinctUntilChanged, filter, finalize, firstValueFrom, map, Subject, take, takeUntil } from 'rxjs';

import { GlobalFunctions, SnackType } from '@global/GlobalFunctions';
import { siteSubscriptions } from '@services/siteSubscriptions';
import { AccountsService, AccountFeatures, Account, IAccount } from '@services/accounts.service';
import { AssetsDataService, LevelAsset } from '@services/assets-data.service';
import { AuthenticationService, WebAppUser } from '@services/authentication.service';
import { Rule, RuleDefinition, RulesDataService } from '@services/rules-data.service';
import { OrganisationsHierarchy } from '@services/organisation-data.service';

import { convertEpochToDateString } from '@functions/functions';
import { IQueryParameterFiltering, QueryParamType } from '@global/iFiltering';

import { CreateRuleComponent, EditRuleDialogData } from 'src/app/dialogs/create-rule/create-rule.component';
import { RuleListComponent, RuleListDialogData } from 'src/app/dialogs/rule-list/rule-list.component';

import { DropDownList } from '@syncfusion/ej2-angular-dropdowns';


/** Used to filter the table of Rules */
export interface FilterQueryParams {
	Enabled?: boolean;
	Disabled?: boolean;
	Active?: boolean;
	CMMS?: boolean;

	selectedTab?: string;

	Location?: string;

	RulesFilter?: string;
	RulespageIndex?: number;
	RulespageSize?: number;

	AssetsFilter?: string;
	AssetspageIndex?: number;
	AssetspageSize?: number;
}


@Component({
	selector: 'app-rules',
	templateUrl: './rules.component.html',
	styleUrls: ['./rules.component.scss']
})
export class RulesComponent implements OnInit, IQueryParameterFiltering<FilterQueryParams> {

	// =========================================================================
	// Inputs / Account Context
	// =========================================================================

	/** accountID */
	@Input() accountGUID: string;

	/** OrganisationID */
	@Input() OrganisationUID: string;

	/** selectedAccount */
	selectedAccount: IAccount;


	// =========================================================================
	// Query Parameter Filtering
	// =========================================================================

	/** Initialised filterQueryParams */
	filterQueryParams: FilterQueryParams = {};

	/**
	 * filterQueryParamTypes
	 *
	 * Definition of the Query Parameter data types
	 */
	filterQueryParamTypes: Record<keyof FilterQueryParams, QueryParamType> = {

		Enabled: 'boolean',
		Active: 'boolean',
		Disabled: 'boolean',
		CMMS: 'boolean',

		selectedTab: 'string',
		Location: 'string',

		RulesFilter: 'string',
		RulespageIndex: 'number',
		RulespageSize: 'number',

		AssetsFilter: 'string',
		AssetspageIndex: 'number',
		AssetspageSize: 'number'

	};

	hasFilters: boolean = false;


	// =========================================================================
	// Core Data
	// =========================================================================

	/** All Rules */
	organisationRules: Rule[] = [];

	/** All Organisation Assets */
	organisationAssets: LevelAsset[] = [];

	/** Organisation Assets with no rules */
	organisationAssetsNoRules: LevelAsset[] = [];

	/** flattenedAssetIDs */
	flattenedAssetIDs: number[] = [];

	/** location Assets array */
	locationAssets: LevelAsset[] = [];


	// =========================================================================
	// Table Configuration
	// =========================================================================

	/** Rule Table fields */
	ruleTableFields: Object = { text: 'RuleDescription', id: 'RuleID' };

	/** Asset Table fields */
	assetTableFields: Object = { text: 'Name', id: 'AssetID' };

	/** ruleTableColumns */
	ruleTableColumns: string[] = [
		'RuleDescription',
		'condition',
		'AssetName',
		'lastTriggered',
		'eventsInPast30Days',
		'CMMSEnabled',
		'buttons',
		'GoToAsset'
	];

	/** assetTableColumns */
	assetTableColumns: string[] = [
		'Name',
		'Description',
		'HierarchyPath',
		'Buttons',
		'GoToAsset'
	];

	/** filteredRuleTableColumns */
	filteredRuleTableColumns: string[] = [];

	/** filteredAssetTableColumns */
	filteredAssetTableColumns: string[] = this.assetTableColumns;


	// =========================================================================
	// Rules Data Source
	// =========================================================================

	/** datasource */
	dataSourceRules: MatTableDataSource<Rule>;

	/** rulesReturned */
	rulesReturned: boolean = false;

	private _paginatorRules: MatPaginator;

	@ViewChild('paginatorRules')
	set paginatorRules(paginator: MatPaginator) {
		try {
			if (!paginator) { return; }

			if (this.filterQueryParams.RulespageSize) {
				paginator.pageSize = this.filterQueryParams.RulespageSize;
			}

			if (this.filterQueryParams.RulespageIndex) {
				paginator.pageIndex = this.filterQueryParams.RulespageIndex;
			}

			this._paginatorRules = paginator;

			if (this.dataSourceRules) {
				this.dataSourceRules.paginator = paginator;
			}
		} catch (error) {
			console.error('Error assigning Rules paginator:', error);
		}
	}

	private _sortRules: MatSort;

	@ViewChild('sortRules')
	set sortRules(sort: MatSort) {
		try {
			if (!sort) { return; }

			this._sortRules = sort;

			if (this.dataSourceRules) {
				this.dataSourceRules.sort = sort;
			}
		} catch (error) {
			console.error('Error assigning Rules sort:', error);
		}
	}


	// =========================================================================
	// Assets Data Source
	// =========================================================================

	/** datasource */
	dataSourceAssets: MatTableDataSource<LevelAsset>;

	/** assetsReturned */
	assetsReturned: boolean = false;

	private _paginatorAssets: MatPaginator;

	@ViewChild('paginatorAssets')
	set paginatorAssets(paginator: MatPaginator) {
		try {
			if (!paginator) { return; }

			if (this.filterQueryParams.AssetspageSize) {
				paginator.pageSize = this.filterQueryParams.AssetspageSize;
			}

			if (this.filterQueryParams.AssetspageIndex) {
				paginator.pageIndex = this.filterQueryParams.AssetspageIndex;
			}

			this._paginatorAssets = paginator;

			if (this.dataSourceAssets) {
				this.dataSourceAssets.paginator = paginator;
			}
		} catch (error) {
			console.error('Error assigning Assets paginator:', error);
		}
	}

	private _sortAssets: MatSort;

	@ViewChild('sortAssets')
	set sortAssets(sort: MatSort) {
		try {
			if (!sort) { return; }

			this._sortAssets = sort;

			if (this.dataSourceAssets) {
				this.dataSourceAssets.sort = sort;
			}
		} catch (error) {
			console.error('Error assigning Assets sort:', error);
		}
	}


	// =========================================================================
	// Location / Hierarchy
	// =========================================================================

	/** locationHierarchyFilter */
	locationHierarchyFilter?: OrganisationsHierarchy;

	/** hierarchy */
	hierarchy: OrganisationsHierarchy[];

	/** hierarchyBreadcrumbs */
	private hierarchyBreadcrumbs: Map<number, string>;

	/** hierarchyAssetIDs */
	private hierarchyAssetIDs: Set<any>;

	// =========================================================================
	// Location Dropdown
	// =========================================================================

	/** dropdownLocations */
	dropdownLocations: OrganisationsHierarchy[] = [];

	/** dropdownLocationFields */
	dropdownLocationFields: { text: string, value: string } = {
		text: 'Name',
		value: 'Uid'
	};

	private _locationDropdown: DropDownList;
	/** locationDropdown */
	@ViewChild('locationDropdown')
	set locationDropdown(dropdown: DropDownList) {
		try {
			if (!dropdown) { return; }

			this._locationDropdown = dropdown;
			dropdown.showClearButton = true;
		} catch (error) {
			console.error('Error assigning location Dropdown:', error);
		}
	}


	// =========================================================================
	// UI / Tabs
	// =========================================================================

	/** List of Tab IDs */
	tabIDList: string[] = ["Rule", "NoRule"];

	/** selectedTabIndex */
	selectedTabIndex: number = 0;


	// =========================================================================
	// User / Feature State
	// =========================================================================

	/** currentUser */
	currentUser: WebAppUser;

	/** CMMSFeatureEnabled */
	CMMSFeatureEnabled: boolean = false;


	// =========================================================================
	// Constructor
	// =========================================================================

	/**
	 * Default Constructor
	 * @param rulesDataService rulesDataService
	 * @param assetsDataService assetsDataService
	 * @param dialog dialog
	 * @param authenticationService authenticationService
	 * @param siteSubscriptions siteSubscriptions
	 * @param router router
	 * @param globalFunctions globalFunctions
	 */
	constructor(
		public rulesDataService: RulesDataService,
		public assetsDataService: AssetsDataService,
		public dialog: MatDialog,
		public authenticationService: AuthenticationService,
		public siteSubscriptions: siteSubscriptions,
		public accountsService: AccountsService,
		private router: Router,
		private route: ActivatedRoute,
		private globalFunctions: GlobalFunctions
	) { }


	// =========================================================================
	// Lifecycle
	// =========================================================================

	/**
	 * Page On Initialise Event Handler
	 */
	ngOnInit(): void {
		this.globalFunctions.displaySpinner(true);

		combineLatest([
			this.siteSubscriptions.selectedAccountGUID$.pipe(),
			this.siteSubscriptions.selectedAccount$.pipe(),
			this.siteSubscriptions.selectedOrganisationUID$.pipe(),
			this.siteSubscriptions.hierarchicalData$.pipe(),
			this.authenticationService.currentUser$.pipe(),
			this.route.queryParamMap.pipe(take(1))
		]).pipe(
			// Ensure that subscriptions are not null or undefined
			filter(([selectedAccountGUID, selectedAccount, selectedOrganisationUID, hierarchicalData, currentUser, queryParamMap]) =>
				!!selectedAccountGUID && !!selectedAccount && !!selectedOrganisationUID && !!hierarchicalData && !!currentUser && !!queryParamMap)
		).subscribe({
			next: async ([selectedAccountGUID, account, selectedOrganisationUID, hierarchicalData, currentUser, queryParamMap]) => {

				this.accountGUID = selectedAccountGUID;
				this.selectedAccount = account;
				this.selectedAccount = this.siteSubscriptions.normalizeFeatures(this.selectedAccount) as Account;
				this.CMMSFeatureEnabled = AccountsService.checkFeature(AccountFeatures.SAP, this.selectedAccount);

				if (!this.CMMSFeatureEnabled) {
					this.filteredRuleTableColumns = this.ruleTableColumns.filter(column => column !== 'CMMSEnabled');
				}
				else {
					this.filteredRuleTableColumns = this.ruleTableColumns;
				}

				this.OrganisationUID = selectedOrganisationUID;
				this.currentUser = currentUser;

				this.hierarchy = JSON.parse(hierarchicalData);
				this.dropdownLocations = [...(this.hierarchy[0].SubChild ?? [])];

				this.hierarchyBreadcrumbs = this.flattenAssetBreadcrumbs(this.hierarchy);
				this.hierarchyAssetIDs = new Set(this.flattenAssets(this.hierarchy).map(a => a.AssetID));

				if (!this.currentUser.AdminSite && !this.selectedAccount.Admin) {
					this.filteredRuleTableColumns.pop(); //hide edit button for non admins
				}

				/** Default to look for only open cases */
				this.filterQueryParams = this.globalFunctions.queryParamsToObject<FilterQueryParams>(
					queryParamMap,
					this.filterQueryParamTypes
				);

				this.selectedTabIndex = this.tabIDList.indexOf(this.filterQueryParams.selectedTab ?? '');

				this.locationHierarchyFilter = this.dropdownLocations.find(
					subchild => subchild.Uid == this.filterQueryParams.Location
				) ?? this.hierarchy[0];


				if (this.locationHierarchyFilter) {
					this.locationAssets = this.flattenAssets([this.locationHierarchyFilter]);
					this.flattenedAssetIDs = this.locationAssets.map(asset => asset.AssetID);
				}

				await Promise.all([
					this.getRules(),
					this.getAssets()
				]);

				this.applyQueryParameters("All", false);

				this.globalFunctions.displaySpinner(false);
			},
			error: (error) => {
				this.globalFunctions.HandleError(error, "Error: Error in one of the combined subscriptions", false);
				this.globalFunctions.displaySpinner(false);
			}
		});
	}


	// =========================================================================
	// Data Loading
	// =========================================================================

	/**
	 * Get the Rules 
	 */
	async getRules() {

		try {
			const rules = await firstValueFrom(
				this.rulesDataService.getRules$(this.accountGUID, this.OrganisationUID, 0, 0).pipe(
					filter(result => !!result),
					map(result => result.filter((rule: Rule) => this.hierarchyAssetIDs.has(rule.AssetID)))
				)
			);

			if (rules) {
				this.organisationRules = rules;
				this.rulesReturned = true;

			}
			else {
				this.globalFunctions.DisplaySnackBar({
					message: "Error Getting Rules",
					duration: 5000,
					action: ""
				});
			}
		}
		catch (error: any) {
			if (error.status != 404) {
				this.globalFunctions.HandleError(error, "this.rulesDataService.getRules$");
			}
		} finally {
			this.assignRulesToDataSource()
		}

	}

	/**
	 * get the Organisation Assets
	 */
	async getAssets() {

		try {

			const assets = await firstValueFrom(
				this.assetsDataService.getOrganisationAssets$(this.accountGUID, this.OrganisationUID)
					.pipe(
						filter((result) => !!result)
					)
			);

			if (assets) {
				this.organisationAssets = (<LevelAsset[]>assets)
				this.organisationAssetsNoRules = this.organisationAssets.filter((x: LevelAsset) => x.RuleCount === 0)
					.map((x: LevelAsset) => {
						x.HierarchyPath = this.hierarchyBreadcrumbs.get(x.AssetID) ?? '';
						return x;
					});
				this.assetsReturned = true;
			}
			else {
				this.globalFunctions.DisplaySnackBar({
					message: "Error Getting Asset List",
					duration: 5000,
					action: ""
				});
			}
		}
		catch (error: any) {
			if (error.status != 404) {
				this.globalFunctions.HandleError(error, "this.assetsDataService.getOrganisationAssets$");
			}
		} finally {
			this.assignAssetsToDataSource();
		}
	}


	// =========================================================================
	// Query Parameter Handling
	// =========================================================================

	/**
	 * Applies the current query parameters to the component's data.
	 *
	 * @param page - The page or data set being queried.
	 * @param reQuery - Determines whether the data should be queried again.
	 */
	applyQueryParameters(page: string = "All", reQuery: boolean = false) {

		if (page == "All" || page == this.tabIDList[0]) {
			// Force MatTableDataSource to re-run filterPredicate
			const currentFilter = this.dataSourceRules.filter;
			this.dataSourceRules.filter = '';
			this.dataSourceRules.filter = currentFilter || this.filterQueryParams.RulesFilter || ' ';
		}

		if (page == "All" || page == this.tabIDList[0]) {
			// Force MatTableDataSource to re-run filterPredicate
			const currentFilter = this.dataSourceAssets.filter;
			this.dataSourceAssets.filter = '';
			this.dataSourceAssets.filter = currentFilter || this.filterQueryParams.AssetsFilter || ' ';
		}

		this.rebuildURL(reQuery);
	}

	/**
	 * Rebuilds the URL using the current query parameter values.
	 *
	 * @param reQuery - Determines whether the data should be queried again
	 * after the URL has been rebuilt.
	 */
	rebuildURL(reQuery: boolean): void {
		if (reQuery) {
			this.router.navigate([], { queryParams: this.filterQueryParams });
		}
		else {
			const urlTree = this.router.createUrlTree([], {
				queryParams: this.filterQueryParams,
				queryParamsHandling: 'merge'
			});

			window.history.replaceState(
				window.history.state,
				'',
				this.router.serializeUrl(urlTree)
			);
		}
	}

	/**
	 * Handles a change to a query parameter.
	 *
	 * @param changed - The name of the query parameter that has changed.
	 */
	onQueryParameterChange(changed: keyof typeof this.filterQueryParams, event: any) {

		this.filterQueryParams.selectedTab ?? "All";

		if (changed == "RulesFilter") {
			const filterValue = (event.target as HTMLInputElement).value;
			this.dataSourceRules.filter = filterValue.trim().toLowerCase();
		}

		if (changed == "AssetsFilter") {
			const filterValue = (event.target as HTMLInputElement).value;
			this.dataSourceAssets.filter = filterValue.trim().toLowerCase();
		}

		if (changed == "selectedTab") {
			this.selectedTabIndex = event.selectedIndex;
			let tabName = this.tabIDList[event.selectedIndex] ?? "";
			this.filterQueryParams.selectedTab = tabName;
		}

		/** Update the Selected Tab */
		let selectedTab = this.filterQueryParams.selectedTab;

		if (changed == "Location") {

			if (this.locationDropdown) {
				this.locationDropdown.showClearButton = true;
			}

			if (event.itemData) {
				this.locationHierarchyFilter = <OrganisationsHierarchy>event.itemData;
				this.filterQueryParams.Location = event.value;
			}
			else if (!event.itemData) {
				this.locationHierarchyFilter = this.hierarchy[0];
				this.filterQueryParams.Location = undefined;
			}

			if (this.locationHierarchyFilter) {
				this.locationAssets = this.flattenAssets([this.locationHierarchyFilter]);
				this.flattenedAssetIDs = this.locationAssets.map(asset => asset.AssetID);
			}

			/** Update all the queries */
			selectedTab = "All";

		}

		this.applyQueryParameters(selectedTab, false);
	}


	/**
	 * onPageChange
	 */
	onPageChange(pag: MatPaginator, event: PageEvent): void {

		if (pag == this._paginatorRules) {
			this.filterQueryParams.RulespageSize = pag.pageSize;
			this.filterQueryParams.RulespageIndex = pag.pageIndex;
		}

		if (pag == this._paginatorAssets) {
			this.filterQueryParams.AssetspageSize = pag.pageSize;
			this.filterQueryParams.AssetspageIndex = pag.pageIndex;
		}

		this.applyQueryParameters("All", false)
	}

	// =========================================================================
	// Data Source Configuration
	// =========================================================================

	/**
	 * assignResultsToDataSource
	 */
	assignRulesToDataSource() {

		if (!this.dataSourceRules) {
			this.dataSourceRules = new MatTableDataSource(this.organisationRules);
		}
		else {
			this.dataSourceRules.data = this.organisationRules;
		}

		this.dataSourceRules.filterPredicate = (rule: Rule, filter: string) => {

			if ((this.filterQueryParams.Active && !rule.Active) ||
				(this.filterQueryParams.Disabled && rule.RuleEnabled) ||
				(this.filterQueryParams.Enabled && !rule.RuleEnabled) ||
				(this.filterQueryParams.CMMS && !rule.CMMSEnabled)) {
				return false;
			}

			// if (this.filterQueryParams.Location) {
			if (!this.flattenedAssetIDs.includes(rule.AssetID)) {
				return false;
			}
			// }

			const query = filter?.toLowerCase().trim() ?? '';
			if (!query) { return true; }

			// Combine the necessary fields for filtering
			const combinedStr = [
				rule.AssetName,
				rule.RuleDescription,
				rule.RuleMessage
			]
				.filter(value => value != null)
				.join(' ')
				.toLowerCase();

			return combinedStr.includes(query)
		};

		this.dataSourceRules.sortingDataAccessor = (item, property) => {
			switch (property) {
				case 'RuleDescription': return item.RuleDescription || '';
				case 'AssetName': return item.AssetName || '';
				case 'lastTriggered': return item.LastTriggered || '';
				case 'eventsInPast30Days': return item.EventsInPast30Days || '';
				default: return '';
			}
		};

	}

	/**
	 * assignAssetsToDataSource
	 */
	assignAssetsToDataSource() {

		if (!this.dataSourceAssets) {
			this.dataSourceAssets = new MatTableDataSource(this.organisationAssetsNoRules);
		}
		else {
			this.dataSourceAssets.data = this.organisationAssetsNoRules;
		}

		this.dataSourceAssets.filterPredicate = (asset: LevelAsset, filter: string) => {

			/* Remove any Leading or following Spaces */
			filter = filter.trim();

			// if (this.filterQueryParams.Location) {
			if (!this.flattenedAssetIDs.includes(asset.AssetID)) {
				return false;
			}
			// }

			const query = filter?.toLowerCase().trim() ?? '';
			if (!query) { return true; }

			// Combine the necessary fields for filtering
			const combinedStr = [
				asset.Name,
				asset.Description
			]
				.filter(value => value != null)
				.join(' ')
				.toLowerCase();

			return combinedStr.includes(query)

		};

		this.dataSourceAssets.sortingDataAccessor = (item, property) => {
			switch (property) {
				case 'Name': return item.Name || '';
				case 'Description': return item.Description || '';
				default: return '';
			}
		};

	}


	// =========================================================================
	// Rule Actions
	// =========================================================================

	/** GoToRule */
	GoToRule(rule: Rule) {
		this.router.navigate([`./${this.accountGUID}/organisation/${this.OrganisationUID}/rules/${rule.RuleID}`])
	}

	/**
	 * getAssetForRule
	 */
	getAssetForRule(rule: Rule, event: MouseEvent) {
		event.stopPropagation();

		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.assetsDataService.getOrganisationAssets$(this.accountGUID, this.OrganisationUID, rule.AssetID).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((ruleEvents) => !!ruleEvents),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
			})
		).subscribe({
			next: (asset: any) => {
				this.onEditRule(rule, asset);
			},
			error: (error) => {
				if (error.status == 404) { }
				else {
					this.globalFunctions.HandleError(error, "Error: AssetsDataService.getOrganisationAssets$", false);
				}
			},
		});
	}

	/**
	 * Edit an existing rule
	 * @param rule
	 * @param event
	 */
	onEditRule(rule: Rule, asset: LevelAsset) {

		// Create a local/'shallow copy of the Asset Information
		var _edit = { ...rule };

		let time: string = this.convertMinutesToHHMM(_edit.TriggerTime)[0] + ":" + this.convertMinutesToHHMM(_edit.TriggerTime)[1];

		let dialogData = <EditRuleDialogData>{
			assetItem: asset,
			ruleItem: _edit,
			triggerTime: time,
			comparison: _edit.RuleText.match(/AND|OR/)?.[0] || ""
		};

		const dialogRef = this.dialog.open(CreateRuleComponent, {
			data: dialogData
		});

		dialogRef.afterClosed().pipe(
			filter(result => !!result)
		).subscribe(async result => {

			if (result.ruleEnabled == null) {
				rule.RuleEnabled = false;
			}
			else {
				rule.RuleEnabled = result.ruleEnabled
			}

			if (result.CMMSEnabled == null) {
				rule.CMMSEnabled = false;
			}
			else {
				rule.CMMSEnabled = result.CMMSEnabled
			}

			rule.RuleDescription = result.ruleDescription;
			rule.RuleMessage = result.ruleMessage;
			rule.TriggerTime = this.timeToMinutes(result.ruleTime);

			//split the rule into the rule-definition composition

			if (rule.RuleDef) {
				let data1;

				if (result.condition1 != '~' && result.condition1 != '!~') {
					data1 = {
						RuleDefID: rule.RuleDef[0].RuleDefID,
						RuleID: rule.RuleID,
						Condition: 1,
						DatapointID: result.dataPoint1,
						Limit: result.value1,
						Operator: result.condition1
					}
				}
				else {
					data1 = {
						RuleDefID: rule.RuleDef[0].RuleDefID,
						RuleID: rule.RuleID,
						Condition: 1,
						DatapointID: result.dataPoint1,
						Limit: result.between1value1,
						LowerLimit: result.between1value2,
						Operator: result.condition1
					}
				}

				let definition1: RuleDefinition = data1;

				let definition2 = undefined;

				/** if we already had another definition */
				if (result.dataPoint2 && rule.RuleDef[1]) {
					let data2;

					if (result.condition2 != '~' && result.condition2 != '!~') {
						data2 = {
							RuleDefID: rule.RuleDef[1].RuleDefID,
							RuleID: rule.RuleID,
							Condition: 2,
							DatapointID: result.dataPoint2,
							Limit: result.value2,
							Operator: result.condition2
						}
					}
					else {
						data2 = {
							RuleDefID: rule.RuleDef[1].RuleDefID,
							RuleID: rule.RuleID,
							Condition: 2,
							DatapointID: result.dataPoint2,
							Limit: result.between2value1,
							LowerLimit: result.between2value2,
							Operator: result.condition2
						}
					}

					definition2 = data2;

				}
				/** if a new definition is being added */
				else if (result.dataPoint2 && !rule.RuleDef[1]) {
					let data2;

					if (result.condition2 != '~' && result.condition2 != '!~') {
						data2 = {
							RuleID: rule.RuleID,
							Condition: 2,
							DatapointID: result.dataPoint2,
							Limit: result.value2,
							Operator: result.condition2
						}
					}
					else {
						data2 = {
							RuleID: rule.RuleID,
							Condition: 2,
							DatapointID: result.dataPoint2,
							Limit: result.between2value1,
							LowerLimit: result.between2value2,
							Operator: result.condition2
						}
					}

					definition2 = data2;
				}

				if (definition2) {
					rule.RuleText = "[1] " + result.comparison + " [2]";
					rule.RuleDef = [definition1, definition2];
				}
				else {
					rule.RuleText = "[1]";
					rule.RuleDef = [definition1];
				}
			}

			await this.patchRule(rule);
		});
	}

	/**
	 * patchRule
	 * @param rule
	 */
	async patchRule(rule: Rule, deleted: boolean = false): Promise<Rule> {
		try {
			const response = await firstValueFrom(
				this.rulesDataService.patchRule$(this.accountGUID, this.OrganisationUID, rule.AssetID, rule, this.currentUser.UserID, deleted)
			);

			this.globalFunctions.DisplaySnackBar({
				message: !deleted ? "Rule Updated" : "Rule Deleted",
				type: SnackType.Info,
				duration: 1000
			});

			return response[0];

		} catch (error: any) {
			if (error.status != 404) {
				console.error(error);
				this.globalFunctions.HandleError(error, "this.rulesDataService.patchRule$: Error retrieving Users")
			}
			return {} as Rule;
		}
		finally { }
	}

	/**
	 * deleteRule
	 */
	deleteRule(rule: Rule) {
		this.globalFunctions.UserConfirm$(`Delete ${rule.RuleDescription}?`).subscribe(async OKResult => {
			if (OKResult) {
				let deletedRule = await this.patchRule(rule, true);
				this.organisationRules = this.organisationRules.filter((x: Rule) => x.RuleID != deletedRule.RuleID);
				this.applyQueryParameters(this.filterQueryParams.selectedTab);
			}
		});
	}

	/**
	 * convertEpoch
	 * @param epoch
	 * @returns
	 */
	convertEpoch(epoch: number): string {
		/** Use the Global Function */
		return convertEpochToDateString(epoch);
	}


	// =========================================================================
	// Asset Actions
	// =========================================================================

	/**
	 * ManageRules
	 * Open Rule List as a pop up dialog and manage rules for the selected asset based on the result
	 */
	ManageRules(selectedAsset: LevelAsset) {
		let data: RuleListDialogData = {
			asset: selectedAsset,
			isAdmin: this.currentUser.AdminSite || this.selectedAccount.Admin,
			currentUser: this.currentUser,
			accountGUID: this.accountGUID,
			organisationUID: this.OrganisationUID,
		}

		const dialogRef = this.dialog.open(RuleListComponent, {
			data: data
		});

		dialogRef.afterClosed().pipe(
			// filter(result => !!result)
		).subscribe(result => {
			this.getRules();
		});
	}

	/**
	 * findAsset
	 * @param assetID assetID  
	 * @returns 
	 */
	getAsset(assetID: number): LevelAsset | undefined {
		return this.organisationAssetsNoRules.find((x: LevelAsset) => x.AssetID == assetID);
	}

	/**
	 * goToAsset
	 * @param row 
	 */
	goToAsset(row: any) {
		let asset = this.getAsset(row.AssetID);
		if (asset) {
			this.siteSubscriptions.SetSelectedAsset(asset);
			this.router.navigate([`./${this.accountGUID}/organisation/${this.OrganisationUID}/location/${asset.ParentUID}/asset/${asset.AssetID}`]);
		}
	}


	// =========================================================================
	// Utility / Conversion
	// =========================================================================

	/**
	 * Converts int of total minutes to [HH,MM] string array
	 * @param totalMinutes
	 * @returns
	 */
	convertMinutesToHHMM(totalMinutes: number): string[] {
		const hours = Math.floor(totalMinutes / 60);
		const minutes = totalMinutes % 60;
		const hoursString = hours.toString().padStart(2, '0');
		const minutesString = minutes.toString().padStart(2, '0');
		return [hoursString, minutesString]
	}

	/**
	 * Converts the HH:MM format required for the trigger time to an int of
	 * total minutes
	 * @param timeStr
	 * @returns
	 */
	timeToMinutes(timeStr: string): number {
		const [hours, minutes] = timeStr.split(':').map(Number);
		const totalMinutes = hours * 60 + minutes;
		return totalMinutes;
	}


	// =========================================================================
	// Hierarchy Helpers
	// =========================================================================

	/** Getting list of assets from an input hierarchy */
	flattenAssets(hierarchy: OrganisationsHierarchy[]): any[] {
		let result: any[] = [];

		// Check if hierarchy is a valid array before proceeding
		if (!Array.isArray(hierarchy)) {
			return result;
		}

		// Iterate over each item in the input hierarchy
		for (const item of hierarchy) {
			// Check if the item has a non-empty 'Asset' property
			if (item.Assets && item.Assets.length > 0) {
				result = result.concat(item.Assets);
			}

			// Recursively add all 'Asset' properties of the item's subchildren
			result = result.concat(this.flattenAssets(item.SubChild));
		}

		return result;
	}

	/**
	 * Returns a map of AssetID -> Hierarchy breadcrumb
	 */
	flattenAssetBreadcrumbs(
		hierarchy: OrganisationsHierarchy[],
		path: string[] = [],
		result = new Map<number, string>()
	): Map<number, string> {

		if (!Array.isArray(hierarchy)) {
			return result;
		}

		for (const item of hierarchy) {

			const currentPath = [...path, item.Name];

			// Store breadcrumb for every asset at this level
			for (const asset of item.Assets ?? []) {
				result.set(asset.AssetID, currentPath.join(' > '));
			}

			// Recurse into children
			this.flattenAssetBreadcrumbs(
				item.SubChild,
				currentPath,
				result
			);
		}

		return result;
	}


	// =========================================================================
	// Display / Filter Messages
	// =========================================================================

	/**
	 * getFilterMessage
	 * @returns 
	 */
	getFilterMessage(): string {
		this.hasFilters = false;
		let showingResult: string = "Showing ";
		let amount: number = 0;
		let hasLocation: boolean = false;

		Object.entries(this.filterQueryParams).forEach(([key, value]) => {
			if (value) {
				this.hasFilters = true;
				if (key != "Location") {
					if (amount == 0) {
						showingResult += " " + key
					}
					else {
						showingResult += ", " + key
					}
					amount++;
				}
			}
		});

		if (this.locationHierarchyFilter) {
			hasLocation = true;
		}

		if (amount == 0) {
			return showingResult + (hasLocation ? `all rules in ${this.locationHierarchyFilter?.Name}` : "all rules");
		}

		return showingResult + (hasLocation ? " rules in " + this.locationHierarchyFilter?.Name : " rules");
	}

	/**
	 * getLocationMessage
	 * @returns 
	 */
	getLocationMessage(): string {
		let result: string = "Showing ";
		let hasLocation: boolean = false;

		if (this.locationHierarchyFilter) {
			hasLocation = true;
		}

		return result + (hasLocation ? "Assets in " + this.locationHierarchyFilter?.Name : "All Assets");
	}

}
